import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Download, CheckCircle, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'tcube_exit_intent_seen_v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let mobileTriggered = false;
    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(STORAGE_KEY)) {
        setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, '1');
      }
    };

    const onScroll = () => {
      if (mobileTriggered) return;
      const ratio = window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight);
      if (ratio > 0.4 && window.innerWidth < 768) {
        mobileTriggered = true;
        setTimeout(() => {
          if (!sessionStorage.getItem(STORAGE_KEY)) {
            setOpen(true);
            sessionStorage.setItem(STORAGE_KEY, '1');
          }
        }, 25000);
      }
    };

    document.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const close = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/email-leads`, { email: trimmed, source: 'exit_intent' });
      setSubmitted(true);
      setTimeout(close, 2000);
    } catch (err) {
      console.error(err);
      setError('Could not save your email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-popup-heading"
      data-testid="exit-intent-popup"
    >
      <div className="relative bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
        <button
          onClick={close}
          aria-label="Close popup"
          data-testid="exit-popup-close-btn"
          className="absolute top-3 right-3 p-1.5 hover:bg-slate-800 rounded-full text-slate-400"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>

        <div className="w-12 h-12 bg-emerald-500/15 rounded-2xl flex items-center justify-center mb-4 border border-emerald-500/30">
          <Download className="w-6 h-6 text-emerald-400" aria-hidden="true" />
        </div>

        <h3 id="exit-popup-heading" className="text-2xl sm:text-3xl font-bold text-white mb-2">
          Before You Go!
        </h3>
        <p className="text-slate-300 text-sm sm:text-base mb-1">
          Get our free doctor-guided health, fitness &amp; nutrition guide.
        </p>
        <p className="text-slate-400 text-sm mb-5">
          Enter your email to download the guide.
        </p>

        {submitted ? (
          <div
            className="flex items-start gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-200"
            data-testid="exit-popup-success"
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-semibold text-sm">Thank you! Check your inbox shortly.</p>
              <p className="text-xs text-emerald-300/80 mt-1">Your guide is on its way.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            <label htmlFor="exit-email" className="sr-only">Email</label>
            <input
              id="exit-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 focus:outline-none rounded-full px-4 py-3 text-white placeholder:text-slate-500 text-sm"
              data-testid="exit-popup-email-input"
            />
            {error && (
              <p className="text-rose-400 text-xs px-1" data-testid="exit-popup-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:opacity-60 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all shadow-lg shadow-emerald-500/25"
              data-testid="exit-popup-submit-btn"
            >
              {submitting ? 'Saving…' : 'Yes, Send Me the Guide'}
            </button>
            <button
              type="button"
              onClick={close}
              className="w-full text-slate-400 hover:text-white text-sm py-1"
              data-testid="exit-popup-dismiss-btn"
            >
              No Thanks
            </button>
            <p className="flex items-center justify-center gap-1.5 text-slate-500 text-[11px] pt-2">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              Your email is securely saved in our admin dashboard.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
